import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import { CVHttpService } from '../services';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    Container,
    Typography,
    Stack,
    TextField,
    Paper,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link as MuiLink
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import { Link } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';

function AICVEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [cv, setCv] = useState(null);
    const [markdown, setMarkdown] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(true); // Show preview by default
    const [error, setError] = useState('');
    const textareaRef = useRef(null);

    useEffect(() => {
        loadCV();
    }, [id]);

    const loadCV = async () => {
        setLoading(true);
        try {
            const data = await CVHttpService.loadCV(id);
            
            // Check if this is an AI-generated CV
            const isAIGenerated = data.description && (
                data.description.trim().startsWith('#') || 
                data.description.includes('##') ||
                (data.description.includes('---') && data.description.split('\n').length > 10)
            );
            
            if (!isAIGenerated) {
                // If not AI-generated, redirect to regular builder
                navigate(`/cv/builder/${id}`);
                return;
            }
            
            setCv(data);
            setMarkdown(data.description || '');
        } catch (err) {
            setError('Failed to load CV');
            console.error('Error loading CV:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!cv?.id) return;
        
        setSaving(true);
        setError('');
        try {
            await CVHttpService.saveCV({
                id: cv.id,
                description: markdown,
                title: cv.title,
                name: cv.name,
                last_name: cv.last_name,
                email: cv.email,
                phone: cv.phone,
                website: cv.website,
                city: cv.city,
                address: cv.address,
                interests: cv.interests,
                country: cv.country?.id
            });
            
            enqueueSnackbar('CV saved successfully!', { variant: 'success' });
            navigate(`/cv/${cv.id}`);
        } catch (err) {
            setError('Failed to save CV');
            enqueueSnackbar('Failed to save CV. Please try again.', { variant: 'error' });
            console.error('Error saving CV:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                </Container>
            </MainLayout>
        );
    }

    if (error && !cv) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                    <Alert severity="error">{error}</Alert>
                </Container>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                <Stack spacing={3}>
                    {/* Breadcrumbs */}
                    <Breadcrumbs aria-label="breadcrumb">
                        <MuiLink
                            component={Link}
                            to="/cv"
                            color="inherit"
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            My CVs
                        </MuiLink>
                        <MuiLink
                            component={Link}
                            to={`/cv/${id}`}
                            color="inherit"
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {cv?.title || 'CV'}
                        </MuiLink>
                        <Typography color="text.primary">Edit Markdown</Typography>
                    </Breadcrumbs>

                    {/* Header */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                            <Box>
                                <Typography variant="h4" fontWeight={700} gutterBottom>
                                    Edit AI-Generated CV
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Edit the markdown content of your AI-generated CV
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => navigate(`/cv/${id}`)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<PreviewIcon />}
                                    onClick={() => setShowPreview(!showPreview)}
                                >
                                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    {error && (
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Editor and Preview */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        {/* Markdown Editor */}
                        <Box sx={{ flex: 1 }}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Markdown Editor
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 2,
                                        position: 'relative',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: '#fafafa',
                                        '&:focus-within': {
                                            borderColor: 'primary.main',
                                            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                >
                                    <Box
                                        component="textarea"
                                        value={markdown}
                                        onChange={(e) => setMarkdown(e.target.value)}
                                        placeholder="# Your Name

## Contact Information
- **Email:** your.email@example.com
- **Phone:** +1234567890
- **Location:** City, Country

## Professional Summary

Your professional summary here...

## Work Experience

### Job Title | Company Name | 2020 - Present
- Achievement or responsibility
- Another achievement

## Education

### Degree | University | 2016 - 2020
- Relevant coursework or achievements"
                                        sx={{
                                            width: '100%',
                                            minHeight: showPreview ? '500px' : '700px',
                                            fontFamily: '"Fira Code", "Consolas", "Monaco", "Courier New", monospace',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.8,
                                            padding: '16px',
                                            border: 'none',
                                            outline: 'none',
                                            resize: 'vertical',
                                            backgroundColor: 'transparent',
                                            color: '#2c3e50',
                                            overflow: 'auto',
                                            scrollbarWidth: 'thin',
                                            '&::-webkit-scrollbar': {
                                                width: '10px'
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f1f1f1',
                                                borderRadius: '4px'
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#888',
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    backgroundColor: '#555'
                                                }
                                            },
                                            '&::placeholder': {
                                                opacity: 0.4,
                                                fontStyle: 'italic',
                                                color: '#888'
                                            },
                                            // Basic syntax highlighting using CSS
                                            '&::selection': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.2)'
                                            }
                                        }}
                                    />
                                </Box>
                                <style>
                                    {`
                                        /* Add basic markdown syntax highlighting hints */
                                        textarea[placeholder*="#"] {
                                            background-image: 
                                                linear-gradient(to right, transparent 0, transparent 100%);
                                        }
                                    `}
                                </style>
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                                        Markdown Syntax Guide:
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        <strong>#</strong> Heading 1 &nbsp;&nbsp; <strong>##</strong> Heading 2 &nbsp;&nbsp; <strong>###</strong> Heading 3<br/>
                                        <strong>**text**</strong> Bold &nbsp;&nbsp; <strong>*text*</strong> Italic<br/>
                                        <strong>-</strong> or <strong>*</strong> Bullet list &nbsp;&nbsp; <strong>---</strong> Horizontal rule
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Preview */}
                        {showPreview && (
                            <Box sx={{ flex: 1 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        Live Preview
                                    </Typography>
                                    <Box 
                                        sx={{ 
                                            mt: 2, 
                                            flex: 1,
                                            minHeight: showPreview ? '500px' : '700px',
                                            maxHeight: 'calc(100vh - 300px)', 
                                            overflowY: 'auto',
                                            p: 2,
                                            bgcolor: '#fafafa',
                                            borderRadius: 1,
                                            '&::-webkit-scrollbar': {
                                                width: '10px'
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f1f1f1',
                                                borderRadius: '4px'
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#888',
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    backgroundColor: '#555'
                                                }
                                            }
                                        }}
                                    >
                                        {markdown.trim() ? (
                                            <MarkdownRenderer markdown={markdown} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Start typing to see the preview...
                                            </Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Container>
        </MainLayout>
    );
}

export default AICVEditorPage;

